import { ApplicationFormError, DealerApplication, DealerApplicationCustomerEmployment, DealerApplicationForm, OtherIncome, getNumberValue, hasValue } from '@amsanalytics/react-shared-library';
import { Alert, Button, Card, Snackbar, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { defaultDealerApplication } from '../../../Models/DealerApplicationForm';
import { AppState } from '../../../Store/rootReducer';
import { loadLibrary, setClient, setLocationId, submitDealerApplication } from '../../../Store/Shared/actions';
import styles from './_dealer-app-form-page.module.scss';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { isNil, reject } from 'ramda';
import { capitalizeFirstLetter, removeHyphensFromString, splitCamelCaseWord } from '../../../Helpers/Utilities';
import { NamedId } from '../../../Models/NamedId';
import LoadingIndicator from '../../Shared/LoadingIndicator/LoadingIndicator';
import { DealerApplicationCustomerWithPrimaryFlag, DealerApplicationSubmission } from '../../../Models/DealerAppSubmission';
import { defaultRelationshipTypes } from '../../../Helpers/Constants';
import { LookupItem } from '../../../Models/LookupItem';
import ApplicationConfirmation from '../ApplicationFormPage/ApplicationConfirmation';
import { useAppDispatch } from '../../../Store/useAppDispatch';

// List of customer titles
const mockCustomerTitles: NamedId[] = [
  {
    id: '1',
    name: 'Mr.',
  },
  {
    id: '2',
    name: 'Ms.',
  },
  {
    id: '3',
    name: 'Mrs.',
  },
];

// List of suffixes
const suffixes: NamedId[] = [
  { id: '1', name: 'Jr.' },
  { id: '2', name: 'Sr.' },
  { id: '3', name: 'I' },
  { id: '4', name: 'II' },
  { id: '5', name: 'III' },
];


// List of intended uses for the Sale vehicle dropdown
const mockVehicleIntendedUses: NamedId[] = [
  { id: '1', name: 'Personal' },
  { id: '2', name: 'Business' },
  { id: '3', name: 'Agricultural' },
  { id: '3', name: 'Hazardous' },
  { id: '3', name: 'Local' },
  { id: '3', name: 'Interstate' },
  { id: '3', name: 'Intermediate' },
];

// Displays list of errors in the form
const DealerFormErrorList = (props: { errors: ApplicationFormError[] }) => {
  return <Card style={{ background: 'white', margin: '-4px', marginLeft: '-8px', marginRight: '-8px', padding: '10px', fontSize: '14px', maxHeight: '300px', width: '200px', overflowY: 'auto' }}>
    <div style={{ marginBottom: '3px' }}>{props.errors.length} required fields:</div>
    {props.errors.map((error: ApplicationFormError, idx: number) => {
      return <div key={idx} style={{ display: 'flex', flexDirection: 'column', marginTop: 0 }}>
        <div style={{ color: '#a02a2e', marginTop: 2, marginBottom: 2, fontWeight: 500, width: 'calc(100% - 10px)', padding: 5, border: 'solid 1px red', background: '#fef3f3' }}>
          {splitCamelCaseWord(capitalizeFirstLetter(error.error))}
        </div>
      </div>
    })}
  </Card>;
};

interface DecodedVehicleInformation {
  year: number;
  make: string;
  model: string;
}

export interface DealerAppFormPageProps { }

export default function DealerAppFormPage(props: DealerAppFormPageProps) {
  // So we can get clientId and locationId from url
  const params = useParams();
  // So we can dispatch actions
  const dispatch = useAppDispatch();
  // So we can navigate to other pages
  const navigate = useNavigate();
  // Location Id
  const locationId = useSelector<AppState, string | null>(appState => appState.shared.locationId);
  // List of states to populate dropdowns
  const states = useSelector((appState: AppState) => appState.shared.states);
  // list of IDs to filter the list of applicant addresses to show in the dropdown
  const applicantAddressStateIdsToFilterBy = useSelector((appState: AppState) => appState.shared.applicantAddressStateIdsToFilterBy);
  // list of IDs to filter the list of employment addresses to show in the dropdown
  const employerAddressStateIdsToFilterBy = useSelector((appState: AppState) => appState.shared.employerAddressStateIdsToFilterBy);
  const identificationTypes = useSelector((appState: AppState) => appState.shared.identificationTypes);
  // List of locations to populate dropdowns
  const housingStatuses = useSelector((appState: AppState) => appState.shared.housingStatuses);
  // List of income periods to populate dropdowns
  const incomePeriods = useSelector((appState: AppState) => appState.shared.incomePeriods);
  // List of bookout sources to populate dropdowns
  const bookoutSources = useSelector((appState: AppState) => appState.shared.bookoutSources);
  // List of income types to populate dropdowns
  const incomeTypes = useSelector((appState: AppState) => appState.shared.incomeTypes);
  // Get disclosure text from lookup items
  const disclosureText = useSelector((state: AppState) => state.shared.disclosure);
  // Job codes
  const jobCodes = useSelector((appState: AppState) => appState.shared.jobCodes);
  // Minimum months of address information
  const minimumMonthsOfAddressInformation = useSelector((appState: AppState) => appState.shared.minimumMonthsOfAddressInformation);
  // Minimum months of employment information
  const minimumMonthsOfEmploymentInformation = useSelector((appState: AppState) => appState.shared.minimumMonthsOfEmploymentInformation);
  // Whether or not the dealer application was created
  const dealerApplicationCreated = useSelector((appState: AppState) => appState.shared.dealerApplicationCreated);
  // All Stores
  const stores = useSelector((appState: AppState) => appState.shared.stores);
  // Redirect URL after submission
  const redirectUrl = useSelector((appState: AppState) => appState.shared.dealerApplicationSubmittedRedirectUrl);
  // list of relationship to applicant types
  const relationshipsToApplicant = useSelector((appState: AppState) => appState.shared.relationshipsToApplicant);
  // Dealer application form
  const [dealerApp, setDealerApp] = useState<DealerApplication>({ ...defaultDealerApplication, transactionTypeId: '1' });
  // Form errors
  const [formErrors, setFormErrors] = useState<ApplicationFormError[]>([]);
  // Whheter or not terms and conditions have been accepted
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  // Whether or not we are decoding the VIN
  const [isDecodingVin, setIsDecodingVin] = useState<boolean>(false);
  // Whether or not the decode Vin snack bar is open
  const [isDecodeSnackbarOpen, setIsDecodeSnackbarOpen] = useState<boolean>(false);
  // VIN decode snackbar message
  const [decodeSnackbarMessage, setDecodeSnackbarMessage] = useState<string>('');
  // client ID
  const clientId = useSelector((appState: AppState) => appState.shared.clientId);
  // Whether or not the form has been submitted
  const [submitted, setSubmitted] = useState<boolean>(false);
  useEffect(() => {
    const clientId = (params as any)?.clientId;
    const amsCode = (params as any)?.amsCode;
    if (hasValue(clientId)) {
      { amsCode ? dispatch(loadLibrary(clientId, amsCode)) : dispatch(loadLibrary(clientId)) };
      dispatch(setClient(clientId));
    }
    if (hasValue(amsCode)) {
      dispatch(setLocationId(amsCode));
    }
  }, [params]);

  // Set the locationId in the app when the list of stores is loaded
  useEffect(() => {
    if (stores.length > 0 && hasValue(locationId)) {
      // set selected locationId in the dealer app form. this will also prevent the user from changing the location in the form.
      setDealerApp({ ...dealerApp, locationId: stores.find(s => hasValue(s.amsCode) && s.amsCode.toUpperCase() === locationId?.toUpperCase())?.id || '' });
      // clear out the location error
      setFormErrors(formErrors.filter(e => e.error !== 'location'));
    }
  }, [stores, locationId]);

  // Listen for when the dealerApplicationCreated flag is set to true. If true, redirect to the redirectUrl
  useEffect(() => {
    if (dealerApplicationCreated === true && hasValue(redirectUrl)) {
      navigate(redirectUrl!);
    }
  }, [dealerApplicationCreated, redirectUrl]);

  // Try to get vehicle information from VIN entered by user. This occurs when user clicks the 'Decode VIN' button in the app form.
  const getVehicleInformationByVin = async () => {
    try {
      setIsDecodingVin(true);
      const response = await fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVin/${dealerApp.saleVehicle.vin}?format=json`);
      if (!response.ok) {
        throw new Error(response.statusText);
      }

      // Pluck out the year, make, and model from the response
      const vinInfo: { Results: { Value: string, ValueId: string, Variable: string, VariableId: number }[] } = await response.json();
      // Determine if there was an error decoding the VIN
      const hasError = vinInfo?.Results.find(r => r.VariableId === 143)?.Value !== "0";

      // If there was no error, update the year, make, and model
      if (!hasError) {
        const yearResult = vinInfo.Results.find(r => r.VariableId === 29)?.Value;
        const make: string = vinInfo.Results.find(r => r.VariableId === 26)?.Value as string;
        const model: string = vinInfo.Results.find(r => r.VariableId === 28)?.Value as string;
        const year: number = yearResult ? parseInt(yearResult) : 0;
        return { year, make, model };
      } else {
        // If there was an error, show a snackbar with the error message
        return undefined;
      }
    } catch (error: any) {
      // catch any errors and show a snackbar with the error message
      return undefined;
    }
  }

  // User clicks 'Decode VIN' button
  const handleDecodeVinClick = async () => {
    await getVehicleInformationByVin().then((decodedInfo: DecodedVehicleInformation | undefined) => {
      if (!isNil(decodedInfo)) {
        // Update Sale Vehicle in the Dealer Application
        setDealerApp({
          ...dealerApp,
          saleVehicle: {
            ...dealerApp.saleVehicle,
            year: decodedInfo.year,
            make: decodedInfo.make,
            model: decodedInfo.model,
          }
        });
        let newErrors = [...formErrors];
        // Remove year, make, and model from the form errors if a value was successfully decoded
        if (hasValue(decodedInfo.year)) {
          newErrors = reject((fe: ApplicationFormError) => fe.fieldName.toUpperCase() === 'YEAR', newErrors);
        }
        if (hasValue(decodedInfo.make)) {
          newErrors = reject((fe: ApplicationFormError) => fe.fieldName.toUpperCase() === 'MAKE', newErrors);
        }
        if (hasValue(decodedInfo.model)) {
          newErrors = reject((fe: ApplicationFormError) => fe.fieldName.toUpperCase() === 'MODEL', newErrors);
        }
        setFormErrors(newErrors);
        // Show snackbar with success message
        setDecodeSnackbarMessage('VIN decoded successfully.');
      } else {
        // Show snackbar with error message
        setDecodeSnackbarMessage('VIN could not be decoded. Please double-check the VIN and try again.');
      }
    }).finally(() => {
      setIsDecodeSnackbarOpen(true);
      setIsDecodingVin(false);
    });
  };

  // Returns true if the user can submit the form, false otherwise. User can submit if:
  // 1. There are no form errors
  // 2. Terms and conditions have been accepted
  // 3. Total months of current address are greater than or equal to the minimum months of address information
  // 4. Total months of current employment are greater than or equal to the minimum months of employment information
  const canSubmitForm = () => {
    return formErrors.length === 0 && termsAccepted
  };

  const calculateAmountToFinance = (): number => {
    return getNumberValue(dealerApp.contractInformation.cashPrice) +
      getNumberValue(dealerApp.contractInformation.taxes) +
      getNumberValue(dealerApp.contractInformation.docFees) +
      getNumberValue(dealerApp.contractInformation.titleRegistrationFees) -
      getNumberValue(dealerApp.contractInformation.cashDown) -
      getNumberValue(dealerApp.contractInformation.rebate) -
      getNumberValue(dealerApp.contractInformation.tradeAllowance) +
      getNumberValue(dealerApp.contractInformation.tradeOwed) +
      getNumberValue(dealerApp.contractInformation.gap) +
      getNumberValue(dealerApp.contractInformation.serviceContract) +
      getNumberValue(dealerApp.contractInformation.creditLife) +
      getNumberValue(dealerApp.contractInformation.disability) +
      getNumberValue(dealerApp.contractInformation.otherInsuranceOrService);
  };

  // User has clicked the 'Submit' button
  const handleSubmitClick = () => {
    // Massage dealer app object before submitting. A few things to do:
    // Map IDs to their corresponding vales for necessary properties.
    // If no co-applicant, set the co-applicant object to undefined
    // If 'Payment Call' transaction type, set Sale Vehicle, Trade Vehicle, and Contract Info to undefined
    const appCustomers: DealerApplicationCustomerWithPrimaryFlag[] = [{
      ...dealerApp.applicant,
      isPrimary: true,
      title: mockCustomerTitles.find((title: NamedId) => title.id === dealerApp.applicant.title)?.name,
      suffix: suffixes.find((suffix: NamedId) => suffix.id === dealerApp.applicant.suffix)?.name,
      ssn: removeHyphensFromString(dealerApp.applicant.ssn),
      housingStatusId: dealerApp.applicant.housingStatusId,
      addresses: [
        ...dealerApp.applicant.addresses.map(a => ({ ...a, state: hasValue(a.state) ? a.state : undefined }))
      ],
      employments: dealerApp.applicant.employments.map((e: DealerApplicationCustomerEmployment) => {
        return {
          ...e,
          state: e.state || undefined
        }
      }),
      otherIncomes: dealerApp.applicant.otherIncomes.map((io: OtherIncome) => {
        return {
          amount: io.amount,
          intervalId: io.intervalId,
          sourceId: io.sourceId,
          startDate: io.startDate,
          endDate: io.endDate,
          comments: io.comments,
        };
      }),
    }];
    if (!isNil(dealerApp.coApplicant)) {
      appCustomers.push({
        ...dealerApp.coApplicant,
        isPrimary: false,
        relationshipCode: relationshipsToApplicant.find((relationship: LookupItem) => relationship.id === dealerApp.coApplicant?.relationshipCode)?.id,
        title: mockCustomerTitles.find((title: NamedId) => title.id === dealerApp.coApplicant?.title)?.name,
        suffix: suffixes.find((suffix: NamedId) => suffix.id === dealerApp.coApplicant?.suffix)?.name,
        ssn: removeHyphensFromString(dealerApp.coApplicant.ssn),
        housingStatusId: dealerApp.coApplicant!.housingStatusId,
        addresses: [
          ...dealerApp.coApplicant.addresses.map(a => ({ ...a, state: hasValue(a.state) ? a.state : undefined, }))
        ],
        employments: dealerApp.coApplicant.employments.map((e: DealerApplicationCustomerEmployment) => {
          return {
            ...e,
            state: e.state || undefined,
          }
        }),
        otherIncomes: dealerApp.coApplicant.otherIncomes.map((io: OtherIncome) => {
          return {
            amount: io.amount,
            intervalId: io.intervalId,
            sourceId: io.sourceId,
            startDate: io.startDate,
            endDate: io.endDate,
            comments: io.comments,
          };
        }),
      });
    }
    const massagedDealerApp: DealerApplicationSubmission = {
      locationId: dealerApp.locationId,
      customers: appCustomers,
      saleVehicle: dealerApp.transactionTypeId === '2' ? undefined : { ...dealerApp.saleVehicle },
      tradeVehicle: dealerApp.transactionTypeId === '2' ? undefined : isNil(dealerApp.tradeVehicle) ? undefined : { ...dealerApp.tradeVehicle },
      contractInformation: dealerApp.transactionTypeId === '2' ? undefined : isNil(dealerApp.contractInformation) ? undefined : { ...dealerApp.contractInformation, amountToFinance: calculateAmountToFinance() },
      comments: dealerApp.comments ? dealerApp.comments : undefined,
      creditScore: dealerApp.creditScore ? dealerApp.creditScore : undefined,
    };
    // submit dealer app and set submitted flag to true.
    dispatch(submitDealerApplication(massagedDealerApp, clientId!));
    setSubmitted(true);
  };

  return submitted === true && dealerApplicationCreated === true
    ? (
      <div style={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ApplicationConfirmation languageId={'1'} />
      </div>
    ) : submitted !== true && dealerApplicationCreated !== true ? (
      <div className={styles.dealerAppFormPageContainer} id='dealer-app-form-container'>
        <DealerApplicationForm
          dealerApplication={dealerApp}
          errors={formErrors}
          isLocationDropdownDisabled={hasValue((params as any)?.amsCode)}
          customerTitles={mockCustomerTitles}
          states={states}
          disclosure={disclosureText || undefined} // dealer app expects undefined if no disclosure text
          applicantAddressStateIdsToFilterBy={applicantAddressStateIdsToFilterBy}
          employerAddressStateIdsToFilterBy={employerAddressStateIdsToFilterBy}
          // identificationTypes={identificationTypes}
          employmentTypes={jobCodes.map(j => { return { id: j.id, name: j.name } })}
          employmentStatuses={incomeTypes.map(j => { return { id: j.id, name: j.name } })}
          locations={stores.map(o => { return { id: o.id, name: o.name } })}
          housingStatuses={housingStatuses.map(h => { return { id: h.id, name: splitCamelCaseWord(h.name), sortOrder: h.sortOrder, description: h.description } })}
          incomePeriods={incomePeriods.map(i => { return { id: i.id, name: i.name, sortOrder: i.sortOrder, description: i.description } })}
          incomeSources={incomeTypes.map(i => { return { id: i.id, name: i.name, sortOrder: i.sortOrder, description: i.description } })}
          relationshipTypes={relationshipsToApplicant}
          suffixes={suffixes}
          vehicleIntendedUses={mockVehicleIntendedUses}
          minAddressMonths={minimumMonthsOfAddressInformation ?? 0}
          minEmploymentMonths={minimumMonthsOfEmploymentInformation ?? 0}
          usePaymentCallTransactionType={false}
          hasAcceptedTerms={termsAccepted}
          isDecodingSaleVin={isDecodingVin}
          bookoutSources={bookoutSources}
          netIncomeLabel={false}
          onChange={(dealerApplication: DealerApplication) => {
            // set the dealer application state
            setDealerApp(dealerApplication);
          }}
          onDecodeButtonClick={() => handleDecodeVinClick()}
          onErrorsUpdate={(errors: ApplicationFormError[]) => setFormErrors(errors)}
          onTermsAccepted={(accepted: boolean) => setTermsAccepted(accepted)}
        />

        {formErrors.length > 0 && (
          <div className={styles.formErrorRow}>
            Please complete these missing fields before submitting your application:
            <Tooltip title={<DealerFormErrorList errors={formErrors} />} placement="top-end" >
              <Button color='error' style={{ textTransform: 'none' }} variant='outlined'>
                <ErrorOutlineOutlinedIcon color='error' style={{ marginRight: '5px' }} />
                Missing Fields
              </Button>
            </Tooltip>
          </div>
        )}

        <div className={styles.formActions}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            disabled={!canSubmitForm()}
            onClick={() => {
              handleSubmitClick();
            }}
          >
            <CheckCircleOutlineOutlinedIcon style={{ marginRight: '5px' }} />
            Submit Application
          </Button>
        </div>
        <Snackbar
          open={isDecodeSnackbarOpen}
          autoHideDuration={5000}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          onClose={() => setIsDecodeSnackbarOpen(false)}
        >
          <Alert
            onClose={() => setIsDecodeSnackbarOpen(false)}
            severity="info"
            sx={{ width: '100%' }}
          >
            {decodeSnackbarMessage}
          </Alert>
        </Snackbar>
      </div>
    ) : <LoadingIndicator label='Submitting your application...' />;
}